/**
 * A ceiling hook: the thing that holds everything else up.
 *
 * It never moves and never hangs. One anchorage, the eye, hanging a little
 * below the beam it is bolted to — the rope meets it there, which is also
 * where the joining dot is, because they are the same point.
 */
import { JOINS, numbered, PartKind } from '../registry';
import { ACROSS } from '../vec';
import { Part, Port, RopeContact, Vec2, World } from '../types';

/**
 * How far the eye sits below the beam.
 *
 * Almost nothing: a hook is bolted TO the ceiling, not slung under it on a
 * stalk. At 0.2 it read as a short dropper with its own length, which is a
 * part the bench does not have and an extra 20 cm nobody asked for in every
 * rope measured from it.
 */
export const ANCHOR_EYE = 0.03;

export interface AnchorData {
    /**
     * A RUNNER: bolted to the beam but free to slide along it.
     *
     * The difference is physics, not drawing. A fixed hook takes whatever
     * sideways pull a slanted rope gives it and the beam swallows it; a runner
     * cannot, so it moves until the rope is straight down — or accelerates
     * forever if it never is. That is the whole of what a trolley on a rail
     * teaches, and it is one flag.
     */
    slides?: boolean;
    /**
     * kg, and only a runner has any.
     *
     * A fixed hook is pinned, so its mass never enters an equation. A runner
     * is a body with one coordinate, and a MASSLESS body free to move with
     * nothing resisting it has no answer at all — the bench would report the
     * arrangement as undecidable the moment a rope under it hung straight
     * down. A trolley has mass; saying so is both true and what makes the
     * question askable.
     */
    mass?: number;
    [k: string]: unknown;
}

export const anchor: PartKind<AnchorData> = {
    key: 'anchor',
    label: 'Ceiling hook',
    home: 0,
    /** It lives on the beam and is dragged along it. */
    onBeam: true,
    create: (at: Vec2, id: string): Part<AnchorData> => ({ id, kind: 'anchor', at: { x: at.x, y: at.y }, data: {} }),
    /**
     * Its eye is AT the hook — a point, like everything else this round.
     *
     * `ANCHOR_EYE` is still what the scene draws the little loop at, because a
     * hook with no loop is a line meeting the ceiling. But a three-centimetre
     * offset in the arithmetic is three centimetres of rope nobody asked for
     * and a place a force can be said to act that is not where the part is.
     */
    ports: (part): Port[] => [{
        id: 'eye', partId: part.id, role: 'anchorage', joins: JOINS.any,
        at: { ...part.at },
    }],
    mass: (part) => (part.data.slides ? (part.data.mass ?? 0.5) : 0),
    extent: () => ({ x: 0.13, y: ANCHOR_EYE + 0.05 }),
    /** Fixed, or free along the beam. An empty axis list IS "fixed" — there is
     *  no separate flag to fall out of step with it. */
    freedom: (part) => (part.data.slides ? { axes: [ACROSS], track: true } : { axes: [] }),
    bondable: true,
    keyFacts: (part) => [part.data.slides ? 'runs on the beam' : 'bolted fast'],
    ropeContact: (part, port): RopeContact => ({ at: port.at }),
    tag: 'H',
    /**
     * Below the bracket and out to the side.
     *
     * A hook has almost nothing free about it: the beam runs across its top,
     * the bracket is a hand's width wide, and its rope drops straight from the
     * eye. Level with the eye the name sat ON the bracket; directly below it
     * would sit on the rope. Down and to the right of both is the one corner
     * nothing else uses.
     */
    nameAt: (part) => ({ at: { x: part.at.x + 0.2, y: part.at.y - 0.17 }, anchor: 'start' as const }),
    name: (part, w) => numbered(part, w, part.data.slides ? 'R' : 'H'),
};
